// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { Box, Card, Switch, Grid, Divider } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { useEffect, useState } from "react";
import { SyncLoader } from 'react-spinners';
import { deviceDisplay } from "modules/inspections/utils/display";

function checkConditionsValues(values, checkSingle = false) {
    var labels = [
        "Vandalism",
        "Unit Damage",
        "Audio Issue",
        "Power Issue",
        "Unresponsive Unit",
        "Location Issue",
        "Other Condition"
    ];

    var conditions = [
        values['devicePrimary-conditions-vandalised'],
        values['devicePrimary-conditions-damaged'],
        values['devicePrimary-conditions-audio'],
        values['devicePrimary-conditions-power'],
        values['devicePrimary-conditions-frozen'],
        values['devicePrimary-conditions-location'],
        values['devicePrimary-conditions-other'].length > 0
    ]

    if (checkSingle) {
        var is_true = conditions.map((r, index) => { return { i: index, v: r } }).filter(r => r.v == true);
        if (is_true.length >= 1) {
            if (is_true.length == 2) {
                return is_true.map(r => labels[r.i]).join(" + ");
            }
            else if (is_true.length == 1) {
                return labels[is_true[0].i];
            }

            return "Inspection & Multiple Conditions";
        }
    }
    else {
        return conditions.filter(r => r == true).length > 0;
    }

    return false;
}

function checkWorks(values, conditionsOther, setFieldValue, inspection_type) {
    const hasStandardConditions = checkConditionsValues(values);
    const otherFieldHasText = values['devicePrimary-conditions-other']?.trim().length > 0;

    const inspectionTitle = checkConditionsValues(values, true);
    setFieldValue(inspection_type.name, inspectionTitle ? inspectionTitle : "General Inspection");

    if (conditionsOther) return otherFieldHasText;
    return hasStandardConditions;
}

function DeviceConditions({ formData, deviceLayout, userPermissions }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setDeviceOptions, setFieldValue, handleChange, handleBack, handleCancel },
        custom: { fetchDevices, activeStep, isLastStep, user, all_devices, deviceOptions }
    } = formData;
    const { devicePrimary, inspection_type } = formField;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const [works, setWorks] = useState(false);
    const [showDevicePrimary, setShowDevicePrimary] = useState(false);
    const [conditionsOther, setConditionsOther] = useState(values?.['devicePrimary-conditions-other']?.trim().length > 0 || false);

    useEffect(() => {
        setWorks(checkWorks(values, conditionsOther, setFieldValue, inspection_type));
    }, [values, conditionsOther]);

    function resetConditions() {
        var condition_types = ['vandalised', 'damaged', 'audio', 'power', 'frozen', 'location', 'other'];

        for (var type of condition_types) {
            setFieldValue(devicePrimary.conditions[type].name, type == 'other' ? "" : false);
        }
    }

    const checkConditionsOther = () => {
        if (!conditionsOther) {
            setFieldValue(devicePrimary.conditions['other'].name, "");
        }
    }

    const conditionItem = (valueKey, title) => {
        var isSelected = values?.[`devicePrimary-conditions-${valueKey}`];

        return (
            <Grid item xxs={6}>
                <SoftBox display="flex" justifyContent="center" py={3} px={1}
                    onClick={(e) => {
                        // console.log(e);
                        setFieldValue(devicePrimary.conditions[valueKey].name, !values[`devicePrimary-conditions-${valueKey}`]);
                    }}
                    sx={({ palette: { gradients }, functions: { rgba, linearGradient } }) => ({
                        borderRadius: '10px',
                        cursor: 'pointer',
                        background: linearGradient(rgba(gradients['light'].main, 0.6), isSelected ? rgba(gradients['error'].main, 0.2) : rgba(gradients['light'].state, 0.6)),
                        border: '1px solid #aaa'
                    })}
                >
                    <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                        <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={deviceLayout == "mobile" ? 'bold' : 'bold'}>{title}</SoftTypography>
                    </SoftBox>
                </SoftBox>
            </Grid>
        )
    };

    return (
        <>
            <SoftBox px={{ xxs: 1, sm: 3 }} pt={{ xxs: 2, sm: 3 }} pb={{ xxs: 0, xl: 3 }}>
                <SoftBox>
                    <SoftBox mb={2} display="flex" flexDirection="column" alignItems="center">
                        <SoftTypography variant="h4" fontWeight="bold">
                            Device Conditions
                        </SoftTypography>
                        <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                            Does the device have any issues or damage?
                        </SoftTypography>
                    </SoftBox>
                    <Divider />
                    <SoftBox mt={1}>
                        <SoftBox display="flex" flex={1} flexDirection='column'>
                            {deviceDisplay(values, isLastStep, 'devicePrimary', fetchDevices?.[values['devicePrimary-uuid']], showDevicePrimary, setShowDevicePrimary, showAdvancedDeviceLevel)}
                            <SoftBox mt={1} mb={2}>
                                <Divider />
                            </SoftBox>
                            <SoftBox display="flex" justifyContent="center" pb={2}>
                                <SoftTypography px={0.25} variant={deviceLayout == "mobile" ? "mobileBody1" : "subtitle2"} fontWeight={deviceLayout == "mobile" ? "bold" : "regular"} sx={{ textAlign: 'center' }}>
                                    Please select the concerning device conditions, or select 'Other' to input another condition. If none apply, skip this section.
                                </SoftTypography>
                            </SoftBox>
                            <Grid container spacing={1}>
                                {conditionItem('vandalised', 'Vandalised')}
                                {conditionItem('damaged', 'Damaged')}
                                {conditionItem('audio', 'Audio Issue')}
                                {conditionItem('power', 'Power Issue')}
                                {conditionItem('frozen', 'Frozen/Unresponsive')}
                                {conditionItem('location', 'Location Issue')}
                                <Grid item xxs={12}>
                                    <SoftBox>
                                        <SoftBox display="flex" justifyContent="center" py={2} px={2}
                                            onClick={(e) => {
                                                setConditionsOther(!conditionsOther);
                                            }}
                                            sx={({ palette: { white, gradients }, functions: { rgba, linearGradient } }) => ({
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                background: linearGradient(rgba(gradients['light'].main, 0.6), conditionsOther ? rgba(gradients['error'].main, 0.2) : rgba(gradients['light'].state, 0.6)),
                                                border: '1px solid #aaa'
                                            })}
                                        >
                                            <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                                                <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={'bold'}>Other</SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                        {conditionsOther && (
                                            <SoftBox>
                                                <FormField
                                                    type={devicePrimary.conditions.other.type}
                                                    label={devicePrimary.conditions.other.label}
                                                    name={devicePrimary.conditions.other.name}
                                                    value={values['devicePrimary-conditions-other']}
                                                    placeholder={devicePrimary.conditions.other.placeholder}
                                                    onChange={handleChange}
                                                />
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                </Grid>
                            </Grid>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
            <SoftBox mt={3}>
                <Divider />
                <SoftBox sx={{ background: '#f9f9f9', borderRadius: '0 0 16px 16px' }} display='flex' justifyContent='space-between' p={1}>
                    <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                        {activeStep === 0 ? "cancel" : "back"}
                    </SoftButton>
                    {works ? (
                        <SoftButton
                            disabled={isSubmitting}
                            type="submit"
                            variant="gradient"
                            color="dark"
                            onClick={(e) => checkConditionsOther()}
                        >
                            {isLastStep ? "submit" : "next"}
                        </SoftButton>
                    ) : (
                        <SoftButton
                            disabled={isSubmitting || (conditionsOther && !values['devicePrimary-conditions-other']?.trim().length)}
                            type="submit"
                            variant="gradient"
                            color="dark"
                            onClick={(e) => resetConditions()}
                        >
                            {isLastStep ? "submit" : "skip"}
                        </SoftButton>
                    )}
                </SoftBox>
            </SoftBox>
        </>
    );
}

// typechecking props for DeviceConditions
DeviceConditions.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
    deviceLayout: PropTypes.string,
};

export default DeviceConditions;
